/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {TextField} from '@material-ui/core';
import {isEmpty as isEmptyObj} from 'lodash';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getAncestorsAccount} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  getCodeMappings,
  getErrorCode,
  hasDiagCode
} from '@ion/core/src/services/JobMetaDefinitions';
import {
  Application,
  Asset,
  Device,
  Devices,
  DownloadJobTask,
  DownloadJobType,
  EntityStatus,
  GenericObject,
  Groups,
  ICNApplications
} from '@ion/core/src/types/entities';
import {
  APIGatewaySearch,
  APIGatewaySmartSearch,
  EmissionTester,
  SmartSearchCriteria
} from '@ion/core/src/types/services';
import {
  calendarDate,
  DateType,
  formatDateTime,
  getEventTime
} from '@ion/core/src/utils/DateUtil';
import {isLMUDevice, isMDTDevice} from '@ion/core/src/utils/DeviceUtils';
import RootManager from '@ion/dls/src/client/RootManager';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {dateTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
import {getLastReportedDate} from '@ion/dls/src/utils/GridUtils';
import {
  AlertEventCode,
  NormalEventCode,
  SentinelDeviceType,
  SentinelTags,
  WarningEventCode
} from './SentinelAssociationTab';
import {CarbColumnType, CarbTabGridDataType, SearchFields} from './types';
import {getDeviceIdentifier} from './ViewDeviceEditorUtils';

/*
 * Defines the required percentage of tasks which must be completed.
 */
const MINIMUM_PERCENTAGE = 15;

interface InitialSentinelNotFoundType {
  identifier?: string;
  error: boolean;
};

/**
 * Defines the reusable initial state for `sentinelNotFound`.
 * Can be used for both for initialization and resetting it to its default values.
 */
export const initialSentinelNotFound: InitialSentinelNotFoundType = {
  identifier : '',
  error      : false
};
  
export const onRenderOption = ({name} = {}) => {
  return <span title={name}>
    {name?.length > 40 ? name?.substring(0, 40) + '...' : name}
  </span>;
};

export const onFileNameRenderOption = ({name, account} = {}) => {
  const value = name?.length > 40 ? name.substring(0, 40) : (name || '');
  const title = account?.title || '';
  return <span title={name}>
    {`${value} (${title})`}
  </span>;
};

export const onRenderInput = (entry: string) => {
  return function(params) {
    return <TextField {...params}
      inputProps={{...params?.inputProps}}
      placeholder={translate(entry)} />;
  };
};

/**
 * Returns the determined last id report value based on the given flags.
 *
 * If explicitLastReport is true, this will pull the lastIdReport from the device extensions
 * 'idReportTimestamp' field ONLY, and if unavailable will return a blank string.
 *
 * If explicitLastReport flag is false, this function will strategically determine the field,
 * defaulting to others before ultimately returning a blank string if none exist.
 */
export const getLastIdReport = (device: Device, explicitLastReport: boolean, latestIdReport): string => {
  if (explicitLastReport) {
    return formatDateTime(device?.extensions?.idReportTimestamp);
  }
  return getLastReportedDate(device, latestIdReport);
};

/**
 * Get a formatted string of a device's last reported time.
 * 
 * ```typescript
 * 
 *   import {getLastReportedTime} from './helpers';
 *   
 *   // Uses default datetime format
 *   const lastReportedTime = getLastReportedTime(device);
 * 
 *   // Explicitly uses default datetime format
 *   const lastReportedTime = getLastReportedTime(device, false);
 * 
 *   // Uses the user's preferred datetime format
 *   const lastReportedTime = getLastReportedTime(device, true);
 * 
 * ```
 * 
 * @param device The device from which to get the last reported time.
 * @param usePreferredDateTimeFormater Whether to use the user's preferred datetime format. Defaults to false.
 * @returns A formatted datetime string representing the given device's last reported time.
 */
export const getLastReportedTime = (device: Device, usePreferredDateTimeFormater = false): string => {
  if (usePreferredDateTimeFormater) {
    return dateTimeFormatter(device?.lastReportedTime) as string;
  }
  return formatDateTime(device?.lastReportedTime);
};

// Builds the smart search for a bulk service enablement request
export const getTotalRowsSearch = (search: SmartSearchCriteria): APIGatewaySmartSearch => ({
  smartSearch : {
    search : {...search.searchCriteria[0].smartSearch}
  }
});

// Builds a search request for a bulk service enablement call with the ids of the given devices.
export const getDeviceIdsSearch = (selectedDevices: Devices): APIGatewaySearch => ({
  searchTerm : {
    search : {
      searchTerms : {
        id : selectedDevices?.map(({id}) => id).join(',')
      }
    }
  }
});

/**
 * Checks if a device is a valid associated device of the given type.
 * 
 * ```typescript
 * 
 *   import {getAssociatedDeviceMobileId} from './helpers';
 *   import {SearchFields} from './types';
 * 
 *   // true if associatedDevice is a valid MDT device
 *   const isValid = isValidAssociatedDevice(associatedDevice, SearchFields.MDT); 
 * 
 *   // true if associatedDevice is a valid LMU device
 *   const isValid = isValidAssociatedDevice(associatedDevice, SearchFields.LMU);
 * 
 *   // false
 *   const isValid = isValidAssociatedDevice(associatedDevice, SearchFields.ANY);
 * 
 * ```
 * 
 * @param type Type of the associated device.
 * @param associatedDevice The associated device to check.
 */
export const isValidAssociatedDevice = (associatedDevice: GenericObject, type: SearchFields): boolean => {
  if (type === SearchFields.MDT) {
    return isMDTDevice({category: associatedDevice?.deviceCategory});
  } else if (type === SearchFields.LMU) {
    return isLMUDevice({category: associatedDevice?.deviceCategory});
  }
  return false;
};

/**
 * Gets the ID of a device with the given type that is associated with the given device.
 * 
 * ```typescript
 * 
 *   import {getAssociatedDeviceMobileId} from './helpers';
 *   import {SearchFields} from './types';
 * 
 *   const associatedDeviceId = getAssociatedDeviceMobileId(device, SearchFields.MDT);
 * 
 *   const associatedDeviceId = getAssociatedDeviceMobileId(device, SearchFields.LMU);
 * 
 * ```
 *  
 * @param device Device whose associated devices are to be searched.
 * @param type Type of the device to get mobile ID from.
 * @returns A string containing the mobile ID of the found associated device. Empty string if none are found.
 */
export const getAssociatedDeviceMobileId = (device: Device, type: SearchFields): string => {
  const foundDevice = device?.extensions?.associatedDevices?.find?.((associatedDevice) =>
    isValidAssociatedDevice(associatedDevice, type));
  return foundDevice?.mobileId || '';
};

export const getHeartBeatInfo = (heartBeatInfo, accountId) => {
  if (heartBeatInfo.length > 0) {
    return {
      accountId     : accountId,
      deviceIdList  : heartBeatInfo,
      accountIdType : 'AccountType',
      deviceIdType  : 'ESN'
    };
  }
};

export const checkICNRole = (applications: Application[]) => {
  return applications?.find(({href}) => {
    const appKey = href?.substring(href?.lastIndexOf('/') + 1);
    return isEqual(appKey, ICNApplications.FirstNoticeOfLoss);
  })?.status === EntityStatus.Enabled;
};

export const getICNHeartBeatData = (devices: Device[], applications: Application[]): string[] => {
  if (checkICNRole(applications) && devices?.length) {
    return devices.filter(({esn}) => !isNull(esn)).map(({esn}) => esn);
  }
  return [];
};

export const getAlertTime = (startDate, endDate, format = 'YYYY-MM-DDTHH:mm:ss.SSSZ'): string => {
  const startDateTime = moment(startDate).format(format);
  const endDateTime   = moment(endDate).endOf('day').format(format);
  return `[${startDateTime} TO ${endDateTime}]`;
};

export const getICNInfo = (
  isHeartbeat: boolean,
  applications: Application[]
): boolean => {
  if (isHeartbeat) {
    return checkICNRole(applications);
  }
  return false;
};

export const createAncestorList = (currentGroupId: number): Promise<GenericObject> => {
  if (!isNil(currentGroupId)) {
    return getAncestorsAccount(currentGroupId).then(result => result);
  }
};

/**
 * Returns ids by batch to perform group search service to retrieve inheritable 
 * property from response.
 */
export const getBatchIds = (devices: Device[]) => {
  const uniqueIds       = new Set();
  const batches         = [];
  const limit           = 100;
  let currentBatchLimit = 0;
  let currentBatch      = '';

  devices?.forEach(({groups = {}}) => {
    groups?.forEach(({href = {}}) => {
      const hrefs = entityId(href);
      if (uniqueIds?.has(hrefs)) {
        return false;
      } if (currentBatchLimit >= limit) {
        batches.push(currentBatch?.slice(0, -1));
        currentBatch      = '';
        currentBatchLimit = 0;
      }
      currentBatch += hrefs + ',';
      currentBatchLimit++;
      uniqueIds?.add(hrefs);
    });
  });

  return {
    currentBatch,
    batches
  };
};

/**
 * Retuns devices array with updating inheritable property.
 */
const getGroups = (device: Device, inheritableGroups: Groups): Groups => {
  return device?.groups?.map(group => {
    const matchedGroup = inheritableGroups?.find(inheritable =>
      entityId(inheritable?._self?.href) === entityId(group?.href)
    );

    if (matchedGroup) {
      return {
        ...group,
        inheritable : matchedGroup?.inheritable,
        account     : matchedGroup?.account
      };
    }
    return group;
  });
};

/**
 * Returns ancestors groups results.
 */
const filterGroups = (updatedGroups: Groups, ancestorsGroups) => {
  return updatedGroups?.filter(({account, inheritable = {}}) => {
    if (inheritable) {
      return !ancestorsGroups?.some(({href}) => entityId(account?.href) === entityId(href));
    }
    return true;
  });
};

/**
 * Returns devices with updated groups results.
 */
export const getUpdatedGroups = (devices: Device[], inheritableGroups, ancestorsGroups) => {
  let filteredGroups      = null;
  const deviceswithGroups = [];

  devices?.map(device => {
    const updatedGroups = getGroups(device, inheritableGroups);
    filteredGroups      = ancestorsGroups?.length ? filterGroups(updatedGroups, ancestorsGroups) : updatedGroups;
    deviceswithGroups.push({...device, groups: filteredGroups || {}});
  });

  return deviceswithGroups;
};

/**
 * The utility checks the progress of a task and returns its percentage. If task is Cancelled,
 * empty string is returned.
 * 
 * @returns {string} - Percentage of the task (Completed, Failed, Pending and Remaining).
 */
export const getJobTasksDetails = ({
  total = 0,
  failed = 0,
  pending = 0,
  completed = 0,
  cancelled = 0
} = {}) => {
  let calculatePercent = '';
  if (total !== cancelled) {
    const activeTotal = total - cancelled;
    const calculate   = (value: number) => {
      return activeTotal ? Math.round((value / activeTotal) * 100) : 0;
    };

    const completedPercent  = calculate(completed);
    const failedPercent     = calculate(failed);
    const inProgressPercent = calculate(pending);

    if (total !== cancelled) {
      if (completedPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${completedPercent}%`;
      } if (failedPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${failedPercent}%`;
      } if (inProgressPercent > MINIMUM_PERCENTAGE) {
        calculatePercent += `${inProgressPercent}%`;
      }
    }
    return calculatePercent.trim();
  }
  return calculatePercent;
};

/**
 * Returns the Job details.
 */
export const getJobDetails = (items) => {
  const {
    jobId,
    username,
    description,
    tasks,
    account,
    progressStatus,
    createdOn
  } = items || {};

  return {
    jobId      : jobId,
    tasks      : tasks?.length,
    devices    : tasks?.[0]?.counts?.total,
    account    : account?.title,
    createdOn  : calendarDate(createdOn),
    jobCreator : username,
    description,
    progressStatus
  };
};

/**
 * Returns the Tasks details.
 */
export const getTaskDetails = (tasks) => {
  const taskData = [];

  tasks?.map(({
    counts: {
      total, completed, pending, failed, cancelled
    }, description = {}, progress: {
      status = {}
    } = {}
  }, index) => {
    taskData.push({
      task            : `${translate('jobsearchengine:taskDetails.task')} ${index+1}`,
      taskDescription : description,
      devicesCount    : total,
      completed       : completed,
      pending         : pending,
      failed          : failed,
      status          : status,
      details         : getJobTasksDetails({total, failed, pending, completed, cancelled}),
      cancelled       : cancelled
    });
  });

  return taskData;
};

/**
 * Returns the Devices details based on matched task index.
 */
export const getDeviceDetails = (matchedDevices) => {
  const deviceData = [];

  const {
    statCode,
    diagCode,
    diagCode32Bit
  } = getCodeMappings();

  matchedDevices?.map?.(({
    device: {
      esn = {}, serialNumber = {}
    } = {}, taskStatus: {
      status = {}, errorCodes = {}, updateTime = null
    } = {}, job : {
      creationDate = null
    } = {}, identifier = {}, error = {}
  }) => (deviceData.push({
    identifier   : getIdentifiers(esn, serialNumber, identifier),
    deviceStatus : status,
    reason       : hasDiagCode(errorCodes?.[0]) ? getErrorCode({
      error : errorCodes?.[0],
      statCode,
      diagCode,
      diagCode32Bit
    }) : errorCodes?.[0],
    lastUpdated : formatDateTime(updateTime || creationDate) || translate('common:notAvailable')
  })));

  return deviceData;
};

/**
 * Returns headers for each tab.
 */
export const getHeadersForTab = (columns, tabName) => {
  const tab = columns?.find(tab => tab?.tabName === tabName);
  return tab ? tab.columns : [];
};

export const getDeviceTab = (device: Device, deviceLength: number) => {
  return {
    tab       : deviceLength,
    tabHeader : getDeviceIdentifier(device),
    data      : device
  };
};

export const getViewDeviceTabs = (selectedDevices) => {
  return selectedDevices?.map((device, index) => {
    return getDeviceTab(device, index);
  });
};

/**
 * Returns the first available identifier, ignoring empty objects.
 */
export const getIdentifiers = (...values) => {
  return values?.find?.(value => value && typeof value !== 'object' && !isEmptyObj(value));
};

/**
 * Determines a color based on the provided battery level.
 *
 * The function maps the battery level to one of three colors: Red,
 * Yellow, or Green, based on the following ranges:
 *
 * - 0% to 20% (inclusive): Returns "Red"
 * - 21% to 80% (inclusive): Returns "Yellow"
 * - 81% to 100% (inclusive): Returns "Green"
 */
export const getColorBasedOnBattery = (batteryLevel) => {
  if (batteryLevel >= 0 && batteryLevel <= 20) {
    return dlsColor('caRed');
  } else if (batteryLevel >= 21 && batteryLevel <= 80) {
    return dlsAlt('maps', 'colors', 'lightOrange');
  } else if (batteryLevel >= 81 && batteryLevel <= 100) {
    return dlsColor('caGreen');
  }
};

/**
 * Returns the value for a given tag label from a dynamic payload
 * structure.
 *
 * The function searches through the provided `dynamicPayload` array,
 * looking for a tag object whose `data` array contains an item with a
 * `label` matching the specified `label` argument. If a matching tag
 * is found, its `value` is returned immediately.
 * 
 * const payload = [
 *   {
 *     tags: [
 *       {
 *         data: [
 *           { label: 'deviceType', value: 'primary' },
 *           { label: 'batteryLevel', value: 85 }
 *         ]
 *       }
 *     ]
 *   }
 * ];
 * getTagValue(payload, 'batteryLevel'); // returns 85
 * getTagValue(payload, 'missingLabel'); // returns []
 * 
 */
export const getTagValue = (dynamicPayload, label) => {
  if (dynamicPayload) {
    for (const payload of dynamicPayload) {
      if (payload?.tags) {
        for (const tag of payload?.tags) {
          const item = tag?.data?.find?.(item => item?.label === label);
          if (item) {
            if (isEqual(label, SentinelTags.TEMPERATURE)) {
              return `${item?.value}${item?.units ?? ''}`;
            }
            return item?.value;
          }
        }
      }
    }
  }
  return [];
};

/**
 * Sorts a list of Sentinel event objects by their event time in
 * descending order (latest first).
 */
export const sortEventsByTime = (events = []) =>
  [...events].sort(
    (a, b) => getEventTime(b?.appMessage?.eventTime) - getEventTime(a?.appMessage?.eventTime)
  );

/**
 * Determines whether a given Sentinel event is marked as 'silent'
 * (SECONDARY_DEVICE_SILENT warning).
 *
 * An event is considered silent if its dynamic payload contains an
 * EVENT_CODE tag with the value `WarningEventCode.SECONDARY_DEVICE_SILENT`.
 */
export const isEventSilent = (event) =>
  event?.appMessage?.dynamicPayload?.some(payload =>
    payload?.tags?.some(tag =>
      tag?.data?.some(field =>
        field?.label === SentinelTags.EVENT_CODE &&
        field?.value === WarningEventCode.SECONDARY_DEVICE_SILENT
      )
    )
  );

/**
 * Extracts the device identifier from an event's payload by looking
 * up the given name tag.
 * 
 * If the device name contains a hyphen, the identifier will be the
 * part after the hyphen.
 */
export const getEventIdentifier = (event, nameKey) => {
  const name = getTagValue(event?.appMessage?.dynamicPayload, nameKey);
  return name?.includes('-') ? name?.split('-')?.[1] : name;
};

/**
 * Checks whether a Sentinel event contains both Battery Level and
 * Temperature fields in its payload.
 */
export const hasBatteryAndTemp = (event) => {
  const dynamicPayload = event?.appMessage?.dynamicPayload;
  return !!getTagValue(dynamicPayload, SentinelTags.BATTERY_LEVEL) &&
         !!getTagValue(dynamicPayload, SentinelTags.TEMPERATURE);
};

/**
 * Returns the latest event from a list of events that matches the
 * specified device type and has a valid paired device name.
 *
 * This function filters the provided `events` array to find events
 * whose dynamic payload matches the given `deviceType` and contains
 * a non-empty device name for the paired device. The function determines
 * the required name tag based on the device type:
 * 
 *   - If `deviceType` is PRIMARY, it checks for a non-empty SECONDARY_DEVICE_NAME.
 *   - If `deviceType` is SECONDARY, it checks for a non-empty PRIMARY_DEVICE_NAME.
 *
 * Among the filtered events, the function sorts them in ascending order
 * by event time and returns the earliest one.
 */
export const getLatestEventByType = (events, device, deviceType) => {
  const mobileId = getSentinelDevice(device);
  const nameKey  = isEqual(deviceType, SentinelDeviceType.PRIMARY)
    ? SentinelTags.SECONDARY_DEVICE_NAME
    : SentinelTags.PRIMARY_DEVICE_NAME;

  const typeMatchedEvents = events?.filter(
    event => getTagValue(event?.appMessage?.dynamicPayload, SentinelTags.DEVICE_TYPE) === deviceType
  ) || [];

  const sortedTypeEvents = sortEventsByTime(typeMatchedEvents);
  const latestEvent      = sortedTypeEvents?.[0];

  let isSilent = isEventSilent(latestEvent);

  const filteredEvents = typeMatchedEvents?.filter(event =>
    mobileId && getEventIdentifier(event, nameKey) === mobileId
  );

  const sortedEvents = sortEventsByTime(filteredEvents);

  if (isSilent) {
    const hasValidPrevEvent = sortedEvents?.some((event, id) =>
      id > 0 && !isEventSilent(event) && hasBatteryAndTemp(event)
    );

    if (!hasValidPrevEvent) {
      isSilent = false;
    }
  }

  let fallbackEvent = null;

  if (isSilent && sortedEvents?.length) {
    fallbackEvent = sortedEvents?.find(event =>
      !isEventSilent(event) && hasBatteryAndTemp(event)
    );
  } else if (!isSilent) {
    fallbackEvent = latestEvent;
  }

  return {
    latestEvent,
    fallbackEvent,
    isSilent
  };
};

/**
 * Extracts and returns the numeric device identifier from a dynamic
 * payload for a given label.
 *
 * This function searches the provided `dynamicPayload` for a tag with
 * the specified `label` (using `getTagValue`). If the tag's value is a
 * string matching the pattern `[A-Za-z]-<digits>` (e.g., "P-6003001123" or "S-12345"),
 * it extracts and returns the numeric portion as a string. If the value does
 * not match the expected pattern or is not found, an empty string is returned.
 * 
 * // Given a dynamic payload with a tag { label: 'deviceId', value: 'P-6003001123' }
 * getDeviceIdentifier(dynamicPayload, 'deviceId'); // returns "6003001123"
 *
 * // If the value does not match the pattern
 * getDeviceIdentifier(dynamicPayload, 'deviceId'); // returns ""
 * 
 */
export const getDeviceIdentifierFromPayload = (dynamicPayload, label) => {
  if (dynamicPayload) {
    const value = getTagValue(dynamicPayload, label);

    if (typeof value === 'string') {
      const match = value?.match(/^[A-Za-z]-(\d+)$/);
      return match ? match?.[1] : '';
    }
  }
};

/**
 * Maps a given event code to a translated Sentinel status label.
 *
 * This function checks the provided `eventCode` against predefined
 * lists of normal, warning, and alert event codes. It returns a
 * translated status string based on which list the event code belongs to:
 *
 * - If the event code is found in the `normalCodes` list, returns the translation for "normal".
 * - If the event code is found in the `warningCodes` list, returns the translation for "warning".
 * - If the event code is found in the `alertCodes` list, returns the translation for "alert".
 * - If the event code is not found in any list, returns `undefined`.
 *
 * The translations are retrieved using the `translate` function with appropriate keys.
 * 
 * mapEventCodeToStatus(NormalEventCode.CODE_11); // returns translated "normal" status
 * mapEventCodeToStatus(WarningEventCode.CODE_10); // returns translated "warning" status
 * mapEventCodeToStatus(AlertEventCode.CODE_12); // returns translated "alert" status
 * 
 */
export const mapEventCodeToStatus = (eventCode) => {
  const normalCodes = [
    NormalEventCode.CODE_11,
    NormalEventCode.CODE_22,
    NormalEventCode.ALARM_CANCEL,
    NormalEventCode.SECONDARY_DEVICE_DETECTED,
    NormalEventCode.PRIMARY_DEVICE_DETECTED
  ];

  const warningCodes = [
    WarningEventCode.CODE_10,
    WarningEventCode.SECONDARY_DEVICE_SILENT
  ];

  const alertCodes = [
    AlertEventCode.CODE_12,
    AlertEventCode.CODE_20,
    AlertEventCode.CODE_21,
    AlertEventCode.PRIMARY_POWER_DISCONNECTED,
    AlertEventCode.PRIMARY_DEVICE_MISSING,
    AlertEventCode.PRIMARY_DEVICE_MISSING_CONTINUE,
    AlertEventCode.ALARM_START,
    AlertEventCode.ALARM_UPDATE
  ];

  if (normalCodes?.indexOf(eventCode) > -1) {
    return translate('admin-devices:view.sentinel.status.normal');
  }
  if (warningCodes?.indexOf(eventCode) > -1) {
    return translate('admin-devices:view.sentinel.status.warning');
  }
  if (alertCodes?.indexOf(eventCode) > -1) {
    return translate('admin-devices:view.sentinel.status.alert');
  }
};

/**
 * Retrieves the `mobileId` of the first associated device tagged as PRIMARY or SECONDARY.
 */
export const getSentinelDevice = (device: Device) => {
  let mobileId            = '';
  const associatedDevices = device?.extensions?.associatedDevices;
  
  if (Array.isArray(associatedDevices)) {
    associatedDevices?.forEach?.(associatedDevice => {
      if (
        (associatedDevice?.tags?.[0] === SentinelTags.PRIMARY) ||
        (associatedDevice?.tags?.[0] === SentinelTags.SECONDARY)
      ) {
        mobileId = associatedDevice?.mobileId;
      }
    });
  }
  return mobileId;
};

/**
 * ```typescript
 * 
 *   import {getParentSortableElement} from './helpers';
 * 
 *   // Gets the main sortable element from its drag handle
 *   const sortableParent = getParentSortableElement(dragHandle);
 * 
 * ```
 * 
 * @see https://docs.dndkit.com/presets/sortable
 * 
 * @param   element Child element of a sortable item.
 * @returns The parent of the given element that contains the sortable 
 *          attributes.
 */
export const getParentSortableElement = (element: HTMLElement): HTMLElement => {
  return RootManager.getParentElementByProperty(element, 'ariaRoleDescription', 'sortable');
};

/**
 * Extracts and returns the failed reason from tasks array
 * if the progress.status of any task is failed.
 * 
 * ```typescript
 * 
 *  import {getReasonFromTasks} from './helpers';
 * 
 *  const faildReason = getReasonFromTasks(job.tasks);
 * 
 * ```
 */
export const getReasonFromTasks = (tasks: DownloadJobTask[] = []): string => {
  const failedTask = tasks.find(
    task => task?.progress?.status?.toLowerCase() === DownloadJobType.Failed.toLowerCase()
  );
  return failedTask?.progress?.reason || '';
};

/**
 * Define initial state value for CARB Tab grid's row data.
 */
export const CarbTabGridInitalState = (
  currentDevice: Device,
  assetDetail: Asset,
  emissionTester: EmissionTester
): CarbTabGridDataType[] => (
  [
    {
      [CarbColumnType.ESN]               : currentDevice?.esn || '',
      [CarbColumnType.VIN]               : assetDetail?.vin || '',
      [CarbColumnType.LICENSE_PLATE]     : assetDetail?.licenseNumber || '',
      [CarbColumnType.LAST_SUBMISSION]   : '',
      [CarbColumnType.EMAIL_ADDRESS]     : emissionTester?.emailAddress || '',
      [CarbColumnType.COUNTRY]           : assetDetail?.country || '',
      [CarbColumnType.STATE]             : assetDetail?.licenseState || '',
      [CarbColumnType.SUBMISSION_STATUS] : ''
    }
  ]
);

// Define the possible CARB Submission Status Type.
export enum StatusType {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  ENABLED = 'enabled'
};

/**
 * Define a function that will return the Submission Status type (Completed, Failed or Pending).
 */
export const getSubmissionStatusType = (status: string = ''): StatusType | string => {
  const str = status.toLowerCase();

  if (str.includes(StatusType.COMPLETED) || str.includes(StatusType.ENABLED)) {
    return StatusType.COMPLETED;
  }

  const mapping: StatusType[] = [
    StatusType.FAILED,
    StatusType.CANCELLED,
    StatusType.PENDING
  ];

  return mapping.find(type => str.includes(type)) || '';
};

/**
 * Define a function that will return a formatted date for the Service Enabled Date
 * section in the CARB tab.
 */
export const serviceEnabledDateFormat = (date: DateType, includeSeconds = false): string => {
  let formattedDate = '';
  if (date) {
    const mDate = moment(date);

    if (mDate.isValid()) {
      const format  = includeSeconds ? 'MM-DD-YYYY - hh:mm:ss A' : 'MM-DD-YYYY - hh:mm A';
      formattedDate = mDate.format(format);
    }
  }
  return formattedDate;
};
